import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { normalizeFileUrl } from "@/lib/attachments/utils";
import { useFrappeFileUrl } from "@/lib/utils/frappeUrl";

type ThumbnailColumnOptions<T> = {
  sizePx?: number; // default 48
  getImagePath?: (row: T) => string | null | undefined;
  getFallbackText?: (row: T) => string | null | undefined;
  onClick?: (row: T) => void;
};

// Muted gray palette for clean, non-distracting placeholder thumbnails
export const pastelColors = [
  { bg: "bg-gray-100 dark:bg-gray-800/50", text: "text-gray-400 dark:text-gray-500" },
];

export function getPastelColor(seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % pastelColors.length;
  return pastelColors[index];
}

export function getInitial(text: string): string {
  const first = text.trim().split(/\s+/)[0] || "";
  return (first[0] || "?").toUpperCase();
}

export type ThumbnailCellProps<T> = {
  rowId: string;
  original: T;
  sizePx: number;
  getImagePath?: (row: T) => string | null | undefined;
  getFallbackText?: (row: T) => string | null | undefined;
  onClick?: (row: T) => void;
};

export function ThumbnailCell<T>({
  rowId,
  original,
  sizePx,
  getImagePath,
  getFallbackText,
  onClick,
}: ThumbnailCellProps<T>) {
  const [imageFailed, setImageFailed] = useState(false);
  const toAbsolute = useFrappeFileUrl();

  // Use getFallbackText if provided, otherwise fall back to rowId (stable identifier)
  // This avoids unsafe `as any` casts and makes the fallback contract explicit.
  const fallbackTextRaw = getFallbackText?.(original) ?? rowId;
  const fallbackText = String(fallbackTextRaw || "");
  const initial = getInitial(fallbackText);
  const color = getPastelColor(fallbackText || rowId);

  const rawPath = getImagePath?.(original);
  // IMPORTANT:
  // - If backend returns an absolute URL (common for gallery/media), do NOT normalize it to a relative path.
  //   Normalizing `http://host/files/x.jpg` -> `/files/x.jpg` breaks when `frappeUrl` isn't configured,
  //   because the browser will then try to load from the current origin (e.g. Vite at :5173).
  // - Only normalize relative/filename inputs.
  const rawPathStr = typeof rawPath === "string" ? rawPath.trim() : "";
  const imageUrl = rawPathStr
    ? rawPathStr.startsWith("http://") ||
      rawPathStr.startsWith("https://") ||
      rawPathStr.startsWith("data:")
      ? rawPathStr
      : (() => {
          const normalized = normalizeFileUrl(rawPathStr);
          return normalized ? toAbsolute(normalized) : null;
        })()
    : null;

  // If the image URL changes (pagination/refetch), allow retrying the image.
  useEffect(() => {
    if (imageUrl) {
      setImageFailed(false);
    }
  }, [imageUrl]);

  const showImage = !!imageUrl && !imageFailed;

  const clickable = typeof onClick === "function";

  return (
    <div className="flex items-center justify-center w-full">
      <button
        type="button"
        disabled={!clickable}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onClick?.(original);
        }}
        className={cn(
          "flex items-center justify-center overflow-hidden",
          "rounded-xs border border-border/60 bg-background",
          clickable ? "cursor-pointer hover:bg-accent/30" : "cursor-default",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1",
          !clickable && "focus:ring-0 focus:ring-offset-0",
        )}
        style={{ width: sizePx, height: sizePx }}
        aria-label={clickable ? `Open ${fallbackText || "record"}` : undefined}
      >
        {showImage ? (
          <img
            src={imageUrl}
            alt={fallbackText || "Thumbnail"}
            className="h-full w-full object-cover"
            loading="lazy"
            decoding="async"
            draggable={false}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <span
            className={cn(
              "flex h-full w-full items-center justify-center font-medium",
              color.bg,
              color.text,
            )}
          >
            {initial}
          </span>
        )}
      </button>
    </div>
  );
}

export function createThumbnailColumn<T>(
  opts: ThumbnailColumnOptions<T> = {},
): ColumnDef<T, unknown> {
  const sizePx = opts.sizePx ?? 48;

  return {
    id: "__thumbnail",
    header: () => (
      <div className="flex items-center justify-center w-full" aria-hidden="true">
        {/* empty */}
      </div>
    ),
    cell: ({ row }) => {
      const original = row.original as T;
      return (
        <ThumbnailCell<T>
          rowId={row.id}
          original={original}
          sizePx={sizePx}
          getImagePath={opts.getImagePath}
          getFallbackText={opts.getFallbackText}
          onClick={opts.onClick}
        />
      );
    },
    enableSorting: false,
    enableColumnFilter: false,
    enableResizing: true,
    size: sizePx + 8,
    minSize: 36,
    maxSize: 120,
    meta: { noPadding: true },
  };
}

